<template>
	<div class="page page--company-invoices flex h-full mt-7">
		<div class="flex-1 flex flex-col h-full overflow-hidden relative">

			<InvoiceList
				v-if="activeTab === 'invoices'"
				:invoices="invoices"
				:invoice-stats="invoiceStats"
				@create="handleCreateNew"
				@edit="handleEdit"
				@duplicate="handleDuplicate"
				@cancel="handleCancelInvoice"
				@delete="handleDeleteInvoice"
				@bulkCancel="handleBulkCancel"
				@view="handleView"
				@dateChanged="handleDateChanged"
			/>

			<InvoiceEditor
				v-if="isEditorOpen"
				:initialInvoice="editingInvoice"
				@save="handleSaveInvoice"
				@cancel="handleEditorCancel"
			/>

			<InvoiceView
				v-if="viewingInvoice"
				:invoice="viewingInvoice"
				:partnerUuid="partnerUUID"
				:partnerToken="partnerToken"
				@close="() => viewingInvoice = null"
			/>
		</div>
		<wc-toast></wc-toast>

		<BasePopup ref="cancelPopup" :withoutIcon="true">
			<template #header>
				<h3 class="text-lg font-semibold text-center">
					{{ confirmTitle }}
				</h3>
			</template>

			<template #content>
				<p class="text-center">
					{{ confirmText }}
				</p>
			</template>

			<template #actions="{ close, confirm }">
				<div class="flex justify-center gap-3">
					<button
						class="px-4 py-2 rounded-lg border border-slate-200"
						@click="close"
					>
						{{ $t('invoices.common.cancel') }}
					</button>

					<button
						class="px-4 py-2 rounded-lg bg-red-600 text-white cursor-pointer"
						@click="confirm(true)"
					>
						{{ $t('invoices.common.confirm') }}
					</button>
				</div>
			</template>
		</BasePopup>
	</div>
</template>

<script>
import InvoiceList from '@vue-app/modules/invoices/components/InvoiceList.vue'
import InvoiceEditor from '@vue-app/modules/invoices/components/InvoiceEditor.vue'
import InvoiceView from '@vue-app/modules/invoices/components/InvoiceView.vue'
import { InvoiceStatus } from '@vue-app/modules/invoices/types'
import { invoicesApi } from '@/office/api/invoices-api'
import { mapInvoiceFromApi, mapInvoiceToApi, mapInvoiceFromDetailsApi, mapInvoiceStatsFromApi } from '@/vue/modules/invoices/mappers/invoice.mapper'
import { notify } from "@/utils/notify.js";
import 'wc-toast'
import BasePopup from '@/vue/components/base/popup/BasePopup.vue'
import { buildApiDateRange } from "@/utils/date.js"

export default {
	name: "CompanyInvoicesModule",
	components: {
		InvoiceList,
		InvoiceEditor,
		InvoiceView,
		BasePopup
	},
	data() {
		const { dateFrom, dateTo } = this.getDefaultDateRange()

		return {
			activeTab: 'invoices',
			defaultDateFrom: dateFrom,
			defaultDateTo: dateTo,

			invoices: [],
			invoiceStats: [],
			isEditorOpen: false,
			editingInvoice: null,
			viewingInvoice: null,
			isLoading: false,
			confirmType: null,
			bulkCancelCount: 0
		}
	},

	computed: {
		confirmTitle() {
			switch (this.confirmType) {
				case 'bulk':
					return this.$t('invoices.confirm_bulk_cancel_title')
				case 'delete':
					return this.$t('invoices.confirm_delete_title')
				default:
					return this.$t('invoices.confirm_cancel_title')
			}
		},

		confirmText() {
			switch (this.confirmType) {
				case 'bulk':
					return this.$t('invoices.confirm_bulk_cancel_text', {
						count: this.bulkCancelCount
					})
				case 'delete':
					return this.$t('invoices.confirm_delete_text')
				default:
					return this.$t('invoices.confirm_cancel_text')
			}
		},

		isCreateRoute() {
			return this.$route.name === 'company-invoice-create'
		},
		companyId() {
			return Number(this.$route.params.id)
		},

		company() {
			return this.$store.state.companies.find(
				c => Number(c.ID) === this.companyId
			)
		},

		partnerUUID() {
			return this.company?.PartnerUUID
		},

		partnerToken() {
			return this.company?.PartnerToken
		},
	},

	async created() {
		try {
			await this.$store.dispatch('LOAD_COMPANIES')

			if (!this.company) {
				console.error('Company not found', this.companyId)
				this.$router.replace('/cabinet')
				return
			}

			this.loadInvoices()
			this.loadInvoiceStats()

		} catch (e) {
			console.error('Init invoices module failed', e)
		}
	},

	methods: {
		async handleDateChanged({ dateFrom, dateTo }) {
			this.defaultDateFrom = dateFrom
			this.defaultDateTo = dateTo

			await this.loadInvoices()
		},

		getDefaultDateRange() {
			const now = new Date()
			const twoDaysAgo = new Date()
			twoDaysAgo.setDate(now.getDate() - 2)

			const format = (d) =>
				d.toISOString().split('T')[0]

			return {
				dateFrom: format(twoDaysAgo),
				dateTo: format(now),
			}
		},

		async loadInvoiceStats() {
			try {
				const response = await invoicesApi.getStats({
					partner_uuid: this.partnerUUID,
					partner_token: this.partnerToken,
				})
				this.invoiceStats = mapInvoiceStatsFromApi(response.data.data)
			} catch (e) {
				console.error('Failed to load invoice stats', e)
			}
		},

		async loadInvoices() {
			this.isLoading = true

			const dateRange = buildApiDateRange(
				this.defaultDateFrom,
				this.defaultDateTo
			)

			try {
				const response = await invoicesApi.getInvoices({
					page: 1,
					...dateRange,
					order: 'desc',
					sort: 'date',
					partner_uuid: this.partnerUUID,
					partner_token: this.partnerToken,
				})

				this.invoices = (response.data.data || []).map(mapInvoiceFromApi)
			} catch (error) {
				console.error('Failed to load invoices', error)
			} finally {
				this.isLoading = false
			}
		},

		handleCreateNew() {
			this.editingInvoice = null
			this.isEditorOpen = true
		},

		async handleEdit(inv) {
			try {
				this.isLoading = true
				const response = await invoicesApi.getInvoice(inv.id, this.partnerUUID, this.partnerToken)
				const fullInvoice = mapInvoiceFromDetailsApi(response.data)

				this.editingInvoice = fullInvoice
				this.isEditorOpen = true
			} catch (e) {
				console.error('Failed to load invoice', e)
			} finally {
				this.isLoading = false
			}
		},

		async handleView(inv) {
			try {
				this.isLoading = true

				const response = await invoicesApi.getInvoice(inv.id, this.partnerUUID, this.partnerToken)
				const fullInvoice = mapInvoiceFromDetailsApi(response.data)

				this.viewingInvoice = fullInvoice
			} catch (e) {
				console.error('Failed to load invoice details', e)
				alert('Failed to load invoice')
			} finally {
				this.isLoading = false
			}
		},

		async handleDuplicate(invoice) {
			const res = await invoicesApi.getInvoice(invoice.id, this.partnerUUID, this.partnerToken)

			const fullInvoice = mapInvoiceFromDetailsApi(res.data)

			const copy = JSON.parse(JSON.stringify(fullInvoice))

			copy.id = null
			copy.number = fullInvoice.number + "-COPY"
			copy.status = InvoiceStatus.DRAFT
			copy.transactions = []
			copy.date = new Date().toISOString().split("T")[0]

			this.editingInvoice = copy
			this.isEditorOpen = true
		},

		async handleBulkCancel(ids) {
			if (!Array.isArray(ids) || ids.length === 0) return;

			this.confirmType = 'bulk'
			this.bulkCancelCount = ids.length
			const confirmed = await this.$refs.cancelPopup.open()

			if (!confirmed) return

			try {
				await invoicesApi.bulkCancelInvoices(ids, this.partnerUUID, this.partnerToken);

				this.invoices = this.invoices.map(inv =>
					ids.includes(inv.id)
						? { ...inv, status: InvoiceStatus.CANCELED }
						: inv
				)

				await this.loadInvoiceStats()

			} catch (e) {
				console.error(e);
				notify.error(this.$t('invoices.errors.cancel_invoices'))
			}
		},

		async handleSaveInvoice(data) {
			try {
				const payload = mapInvoiceToApi(data, this.partnerUUID, this.partnerToken)

				let response
				let savedInvoice

				if (data.id) {
					response = await invoicesApi.updateInvoice(data.id, payload)
					savedInvoice = mapInvoiceFromDetailsApi(response.data)

					const idx = this.invoices.findIndex(i => i.id === savedInvoice.id)

					if (idx !== -1) {
						this.invoices.splice(idx, 1, savedInvoice)
					}

				} else {
					response = await invoicesApi.createInvoice(payload)
					savedInvoice = mapInvoiceFromDetailsApi(response.data)

					this.invoices.unshift(savedInvoice)
				}

				this.isEditorOpen = false

			} catch (e) {
				console.error('Failed to save invoice', e)
				notify.error(this.$t('invoices.errors.save_invoices'))
			}
		},

		handleEditorCancel() {
			this.isEditorOpen = false
			this.$router.replace({
				path: `/cabinet/company/${this.companyId}/invoices`,
				query: {}
			})
		},

		async handleCancelInvoice(invoice) {
			if (!invoice?.id) return;

			this.confirmType = 'single'
			const confirmed = await this.$refs.cancelPopup.open()

			if (!confirmed) return

			try {
				await invoicesApi.cancelInvoice(invoice.id, this.partnerUUID, this.partnerToken);

				const idx = this.invoices.findIndex(i => i.id === invoice.id)
				if (idx !== -1) this.invoices[idx] = { ...this.invoices[idx], status: 'canceled' }

			} catch (e) {
				console.error(e);
				notify.error(this.$t('invoices.errors.cancel_invoices'))
			}
		},

		async handleDeleteInvoice(id) {
			if (!id) return;

			this.confirmType = 'delete'
			const confirmed = await this.$refs.cancelPopup.open()

			if (!confirmed) return

			try {
				await invoicesApi.deleteInvoice(id, this.partnerUUID, this.partnerToken);

				this.invoices = this.invoices.filter(inv => inv.id !== id)
			} catch (e) {
				console.error(e);
				notify.error(this.$t('invoices.errors.delete_invoices'))
			}
		},
	},
	watch: {
		async companyId(newId, oldId) {
			if (newId === oldId) return

			if (!this.company) return

			this.invoices = []
			this.invoiceStats = []

			await this.loadInvoices()
			await this.loadInvoiceStats()
		},
		'$route.query.create': {
			immediate: true,
			handler(val) {
				if (val === '1') {
					this.editingInvoice = null
					this.isEditorOpen = true
				} else {
					this.isEditorOpen = false
				}
			}
		}
	},
}
</script>
